import type { Moment } from 'moment';
import momentLib from 'moment-timezone';

let configuredZone: string | null = null;

/**
 * Shop APIs often return fixed-offset labels like `GMT-5` instead of IANA (`America/Chicago`).
 * Colloquial "GMT-5" means UTC−5; in the tz database that fixed offset is `Etc/GMT+5`
 * (the sign in `Etc/GMT*` is the opposite of the usual "UTC±" wording).
 *
 * Returns null if the string cannot be mapped to a zone known to moment-timezone.
 */
export function normalizeApiTimeZoneToIANA(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;

  if (momentLib.tz.zone(s)) return s;

  if (/^(GMT|UTC)$/i.test(s)) return 'Etc/UTC';

  const m = s.match(/^(?:GMT|UTC)\s*([+-])\s*(\d{1,2})(?::(\d{2}))?\s*$/i);
  if (!m) return null;

  const sign = m[1];
  const hours = parseInt(m[2], 10);
  const minutes = m[3] != null ? parseInt(m[3], 10) : 0;
  if (Number.isNaN(hours) || Number.isNaN(minutes) || hours > 14 || minutes > 59) return null;

  if (minutes !== 0) {
    // No stable IANA "Etc/GMT…" name for arbitrary sub-hour offsets; caller should send IANA or whole-hour GMT.
    return null;
  }

  const etcSign = sign === '+' ? '-' : '+';
  const iana = `Etc/GMT${etcSign}${hours}`;
  return momentLib.tz.zone(iana) ? iana : null;
}

function resolveConfiguredZone(apiTimeZone: string): string | null {
  const trimmed = apiTimeZone.trim();
  if (!trimmed) return null;
  if (momentLib.tz.zone(trimmed)) return trimmed;
  return normalizeApiTimeZoneToIANA(trimmed);
}

/**
 * Applies the shop timezone from the API (IANA or `GMT±N` / `UTC±N`).
 * Plain `moment()` (no arguments) is routed to the configured zone via the default export proxy.
 */
export function configureMomentTimezone(apiTimeZone: string | null | undefined): void {
  if (!apiTimeZone) return;
  const iana = resolveConfiguredZone(apiTimeZone);
  if (!iana) return;
  configuredZone = iana;
  momentLib.tz.setDefault(iana);
}

export function getConfiguredMomentTimezone(): string | null {
  return configuredZone;
}

const moment = new Proxy(momentLib, {
  apply(_target, thisArg, argArray: unknown[]) {
    if (argArray.length === 0 || (argArray.length === 1 && argArray[0] === undefined)) {
      return momentLib.tz();
    }
    return Reflect.apply(momentLib as (...args: unknown[]) => Moment, thisArg, argArray);
  },
}) as typeof momentLib;

export default moment;
